/**
 * The things that need a second look.
 *
 * The one screen an owner will actually read every day, so it is ordered by
 * money at stake rather than by time — a chronological list buries the large
 * loss under nine small corrections. That ordering comes from the API; this
 * file only filters and renders.
 *
 * Each flag carries its own evidence: what happened, how much, who, and when.
 * A warning an owner cannot act on trains them to ignore warnings.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Eye } from 'lucide-react';
import { Badge, EmptyState } from '../../components/ui';
import { money, moment } from './format';
import type { Flag, FlagKind } from './types';

const KIND_LABELS: Record<FlagKind, string> = {
  stock_removed: 'Stock removed without a sale',
  void: 'Voided sale',
  cash_shortage: 'Cash shortage',
  uncounted: 'No cash count',
  negative_stock: 'Impossible stock level',
};

/**
 * Why each one matters, in a sentence.
 *
 * An owner who has never run a till does not know that a voided sale is the
 * classic way cash walks. Saying so, once, beside the flag, is the difference
 * between a list of jargon and a tool.
 */
const KIND_EXPLANATIONS: Record<FlagKind, string> = {
  stock_removed:
    'Goods left the shelf without being sold. Normal for breakage or spoilage — worth checking when the value is large.',
  void: 'A sale was cancelled after being recorded. Ringing up a sale, taking the cash and voiding it leaves the books looking right and the drawer short.',
  cash_shortage: 'Less cash was counted than the sales say there should be.',
  uncounted: 'Nobody counted the drawer. A shortage cannot be noticed on a day nobody counts.',
  negative_stock:
    'More was sold than was ever received, which is impossible — so a delivery was never entered, or sales were recorded against the wrong product.',
};

export function FlagList({
  flags,
  currency,
  timezone,
}: {
  flags: Flag[];
  currency: string;
  timezone: string;
}): ReactNode {
  const [severity, setSeverity] = useState<'all' | 'investigate' | 'watch'>('all');
  const [kind, setKind] = useState<'all' | FlagKind>('all');
  const [staff, setStaff] = useState<string>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  const staffNames = useMemo(
    () => [...new Set(flags.map((f) => f.staffName).filter((n): n is string => Boolean(n)))],
    [flags],
  );

  const visible = flags.filter(
    (f) =>
      (severity === 'all' || f.severity === severity) &&
      (kind === 'all' || f.kind === kind) &&
      (staff === 'all' || f.staffName === staff),
  );

  if (flags.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 className="h-6 w-6" />}
        title="Nothing needs your attention"
        description="No stock left without a sale, no voided sales, and the cash was counted."
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Filters scroll rather than wrap, so the list stays above the fold. */}
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value as typeof severity)}
          aria-label="Filter by severity"
          className="shrink-0 rounded-lg border border-slate-200 px-2 py-1 text-xs"
        >
          <option value="all">All severities</option>
          <option value="investigate">Needs attention</option>
          <option value="watch">Worth watching</option>
        </select>

        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as typeof kind)}
          aria-label="Filter by type"
          className="shrink-0 rounded-lg border border-slate-200 px-2 py-1 text-xs"
        >
          <option value="all">All types</option>
          {(Object.keys(KIND_LABELS) as FlagKind[]).map((k) => (
            <option key={k} value={k}>
              {KIND_LABELS[k]}
            </option>
          ))}
        </select>

        {staffNames.length > 0 && (
          <select
            value={staff}
            onChange={(e) => setStaff(e.target.value)}
            aria-label="Filter by staff"
            className="shrink-0 rounded-lg border border-slate-200 px-2 py-1 text-xs"
          >
            <option value="all">Everyone</option>
            {staffNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title="Nothing matches those filters"
          description="Change or clear the filters to see the rest."
        />
      ) : (
        <ul className="space-y-2">
          {visible.map((flag, index) => {
            const id = `${flag.kind}-${flag.entityId ?? index}`;
            const critical = flag.severity === 'investigate';

            return (
              <li
                key={id}
                className={`rounded-xl border p-3 ${
                  critical ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50/50'
                }`}
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle
                    className={`mt-0.5 h-4 w-4 shrink-0 ${critical ? 'text-rose-600' : 'text-amber-600'}`}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <Badge tone={critical ? 'danger' : 'warning'}>
                        {critical ? 'Needs attention' : 'Worth watching'}
                      </Badge>
                      <span className="text-xs text-slate-600">{KIND_LABELS[flag.kind]}</span>
                    </div>

                    <p className="mt-1 text-sm font-medium text-slate-900">{flag.summary}</p>

                    <p className="mt-0.5 text-xs text-slate-600">
                      {flag.value !== null && (
                        <span className="font-medium tabular-nums">
                          {money(flag.value, currency)}
                        </span>
                      )}
                      {flag.staffName && <> · {flag.staffName}</>}
                      {flag.occurredAt && <> · {moment(flag.occurredAt, timezone)}</>}
                    </p>

                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === id ? null : id)}
                      aria-expanded={expanded === id}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-700"
                    >
                      <Eye className="h-3.5 w-3.5" aria-hidden />
                      {expanded === id ? 'Hide details' : 'View details'}
                    </button>

                    {expanded === id && (
                      <div className="mt-2 space-y-1 rounded-lg bg-white/70 p-2 text-xs text-slate-700">
                        <p>{KIND_EXPLANATIONS[flag.kind]}</p>
                        <dl className="grid grid-cols-2 gap-1">
                          {flag.value !== null && (
                            <>
                              <dt className="text-slate-500">Amount</dt>
                              <dd className="tabular-nums">{money(flag.value, currency)}</dd>
                            </>
                          )}
                          {flag.staffName && (
                            <>
                              <dt className="text-slate-500">Recorded by</dt>
                              <dd>{flag.staffName}</dd>
                            </>
                          )}
                          {flag.occurredAt && (
                            <>
                              <dt className="text-slate-500">When</dt>
                              <dd>{moment(flag.occurredAt, timezone)}</dd>
                            </>
                          )}
                          {flag.entityId && (
                            <>
                              <dt className="text-slate-500">Reference</dt>
                              <dd className="truncate font-mono">{flag.entityId.slice(0, 8)}</dd>
                            </>
                          )}
                        </dl>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
